import { defineComponent, computed } from "vue";
import { Container } from "aurelia-dependency-injection";
import useVuelidate from "@vuelidate/core";
import { minLength, required } from "@vuelidate/validators";

import { CentralIntakeService } from "../../../../../services/central-intake.service";
import { PatientService } from "../../../../../services/patient.service";
import { ToastrService } from "../../../../../services/toastr.service";
import { IGetInsuranceQuery } from "../../../../../resources-vue/vue-interfaces/i-get-referral";
import { RelationshipToInsured } from "../../../../../enums/enums";
import { HomeHealthReferral } from "../../../../../resources-vue/vue-models/homehealth-referral";
import {
    IHomeHealthReferralOld,
    IHomeHealthReferralExtension
} from "../../../../../resources-vue/vue-interfaces/i-homehealth-referral";
import { IGetPayorByBranchID } from "../../../../../resources-vue/vue-interfaces/i-patient-payor";
import { IPatientValidation } from "../../../../../resources-vue/vue-interfaces/i-patient";

import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import MedicarePayorNew from "../MedicarePayorNew/MedicarePayorNew.vue";
import NonMedicarePayorNew from "../NonMedicarePayorNew/NonMedicarePayorNew.vue";
import VerifyEligibility from "../../../../../resources-vue/vue-dialogs/VerifyEligibility/VerifyEligibility.vue";

enum Insurer {
    Medicare = "medicare",
    NonMedicare = "nonMedicare"
}

export default defineComponent({
    components: {
        FormSection,
        MedicarePayorNew,
        NonMedicarePayorNew
    },

    setup() {
        return { v$: useVuelidate() };
    },

    validations() {
        return {
            referral: {
                Referral: {
                    MbiNumber: !this.payorInfoNotapplicable.MbiNumber
                        ? { required, minLength: minLength(11) }
                        : { required: false },
                    MedicaidNumber: !this.payorInfoNotapplicable.MedicaidNumber ? { required } : { required: false }
                }
            }
        };
    },

    props: {
        referral: {
            type: HomeHealthReferral,
            default: null
        },
        referralOptions: {
            type: Object,
            default: null
        },
        isEditing: {
            type: Boolean,
            default: false
        },
        isRefer: {
            type: Boolean,
            default: false
        },
        branchId: {
            type: String,
            default: ""
        }
    },

    data() {
        return {
            _centralIntakeService: null,
            _patientsService: null,
            dialogRef: null,
            defaultInsurerRelation: "2",
            relationshipToInsuredEnum: RelationshipToInsured,
            primaryInsurance: [] as IGetInsuranceQuery[],
            secondaryInsurance: [] as IGetInsuranceQuery[],
            tertiaryInsurance: [] as IGetInsuranceQuery[],
            referralCache: null,
            _toastrService: null,
            isCheckingEligibility: false,
            eligibilityMissingUrl: {
                locationId: "#Referral_Agency_Branch_Label",
                firstName: "#Referral_First_Name_Label",
                lastName: "#Referral_Last_Name_Label",
                gender: "#Referral_Gender_Label",
                dateOfBirth: "#Referral_Date_Of_Birth_Label",
                medicareNumber: "#Referral_Medicare_Number_Label"
            },
            payorInfoNotapplicable: {
                MbiNumber: false,
                HicNumber: false,
                MedicaidNumber: false,
                AlternateMedicaidNumber: false
            },
            payorInfoValues: {
                MbiNumber: "",
                HicNumber: "",
                MedicaidNumber: "",
                AlternateMedicaidNumber: ""
            },
            medicareInsurance: ["1", "2", "3", "4", "5"],
            insurerInfo: null,
            mbiDuplicateCache: null,

            mbiTooltip: `<strong>The Medicare Beneficiary Identifier (MBI) must comply with the following format specifications:</strong>
                         <ul> <li>The MBI must be eleven characters in length.</li>
                         <li>The MBI is case-sensitive and must include capital letters.</li>
                         <li>The first character must be numeric, excluding zero (0).</li>
                         <li>The second, fifth, eighth and ninth characters must be alphabetic (excluding the following letters: S, L, O, I, B, Z).</li>
                         <li>The fourth, seventh, tenth and eleventh characters must be numeric.</li>
                         <li>The third and sixth characters must be numeric or alphabetic (excluding the following letters: S, L, O, I, B, Z).</li>
                         </ul>`,
            payerSourceTooltip: `<h6>Item-Specific Instructions</h6>
                                  <p><strong>A1400. Payor Information</strong></p>
                                  <p>Check the box(es)that best correspond(s) to the patient’s current existing payment sources. Check all that apply.</p>
                                  <p>-This item is intended to identify all payors that the patient has, regardless of whether or not the payor is expected or likely to provide reimbursement for any services, supplies, medications, etc. that the patient
                                  may receive during the hospice episode of care. </p>
                                  <p>-Although Item A1400 is intended to reflect all current, existing payor sources, pending payor sources should not be
                                  included in A1400 (i.e., do not report payment source(s) which have been applied for but not yet received).</p>
                                  <ul><li>Item A1400 is intended to reflect all current existing payor sources, even if the payor is not expected or likely to provide reimbursement. For example, if a Medicare beneficiary was a Medicare Advantage patient prior
                                  to electing the hospice benefit, the hospice should still select “B. Medicare (managed care/Part C/Medicare Advantage)” as one of the existing payment sources for
                                  A1400 even though Medicare Advantage may not reimburse the hospice directly for any services.</li>
                                  <li>It is recommended that providers take efforts to validate existing pay sources (e.g., ask patients to present their Medicare card); however, for the purposes of completing Item A1400, eligibility for or existence of pay sources can be
                                  based on patient/caregiver report. </li>
                                  <li>For the purposes of completing Item A1400, existence of pay sources can be based on patient/caregiver report, and gathering
                                  additional supplementary financial information is not likely to be necessary. It is possible to collect the information during the referral/intake/admission process
                                  when verifying insurance sources, or during preadmission/admission discussions of what the hospice benefit will cover and what the patient may be responsible for.
                                  These conversations may take place as part of an overall assessment of the patient’s ability to pay for such items that may not traditionally be paid for by the Hospice Benefit. </li>
                                  <li><strong>Response A, Medicare (traditional fee-for-service)</strong> should be selected if the patient is a traditional fee-for-service Medicare patient. This includes if the patient has Medicare Part D.
                                  <li><strong>Response B, Medicare (managed care/Part C/Medicare Advantage)</strong> should be selected if the patient has Medicare HMO/managed care, another Medicare Advantage Plan, or Medicare Part C. If the patient was a Medicare Advantage patient prior to enrolling in hospice, select response option B even though Medicare Advantage may not reimburse the hospice directly for any services. If the patient was Part C/Medicare Advantage prior to enrolling in hospice, select the response options for BOTH part C and traditional fee-for-service, as applicable.</li>
                                  <li><strong>Response C, Medicaid (traditional fee-for-service)</strong> should be selected if the patient has traditional fee-for-service Medicaid.</li>
                                  <li><strong>Response D, Medicaid (managed care)</strong> should be selected if the patient has Medicaid managed care.</li>
                                  <li><strong>Response G, Other government (e.g., TRICARE, VA, etc.)</strong> should be selected if the patient has a government plan besides Medicare and/or Medicaid. This would include TRICARE, VA, etc. HIS Manual Chapter 2: Section A V2.01 Effective November 15, 2018 Page 2A-15 Item-Specific Tips (continued)</li>
                                  <li><strong>Response H, Private Insurance/Medigap</strong> should be selected if the patient has available any private insurance. This would include commercial plans irrespective of how they were purchased (i.e., regardless of whether they are provided through an employer or purchased individually by the patient or through a health insurance exchange, etc.). This response option should also be used for prescription drug coverage from a private insurer in addition to/other than Medicare Part D coverage.</li>
                                  <li><strong>Response I, Private Managed Care</strong> should be selected if the patient has available any private insurance that is a managed care plan. This would include commercial or privately purchased managed care plans (e.g., commercial HMO or PPO plans) irrespective of how they were purchased (i.e., regardless of whether they are provided through an employer or purchased individually by the patient or through a health insurance exchange, etc.).</li>
                                  <li><strong>Response J, Self-pay</strong> should be selected if the patient has any amount of personal funds available to contribute to healthcare expenses (services, supplies, medications, etc.) during the hospice episode of care. For purposes of completing Item A1400, the minimum threshold for a “self-pay” patient would be ability to pay for any low-cost medication, supply or service (e.g., medication co-pay or overthe-counter medication). Based on this definition of self-pay, for certain providers, a large majority of patients may be identified as self-pay; this is acceptable. SelfPay should be chosen even if the patient is not actively paying for anything, but could pay for something or has the funds if needed. Additionally, the intent of the self-pay response option is not to assess patients’ ability to self-pay, but rather to determine availability of funds to cover costs of care. Selecting the self-pay response option obligates neither the hospice nor the patient to use those funds to pay for care, should a need to self-pay arise. In this sense, collection of data to complete Item A1400 should not influence the delivery of hospice services based on the patient’s ability to self-pay for care, or based on availability of other pay sources the patient may have.</li>
                                  <li><strong>Response K, No payor source</strong> should be selected if the patient does not have any of the payor sources in response options A-I available, nor do they have any personal funds available (response option J, Self-pay) to contribute to healthcare expenses (services, supplies, medications, etc.) during the hospice episode of care.</li>
                                  <li><strong>Response X, Unknown</strong> should be selected if the patient is not confirmed to have any of the above payor sources in response options A-K available to contribute to healthcare expenses (services, supplies, medications, etc.) during the hospice episode of care.</li>
                                  <li><strong>Response Y, Other</strong> should be selected if the patient has available one or more payor sources that is not listed in responses options A-K above to contribute to healthcare expenses (services, supplies, medications, etc.) during the hospice episode of care.</li>
                                  <li>For charity patients: For the purposes of completing the HIS, there are two types of charity care patients: patients that will receive funds from a funded charity care program and patients that have available no other pay source and are not part of a funded charity care program. Select response “Y, Other” if the patient has available as a pay source a funded charity program. Select response “K, No payor source” if a patient does not have available any of the pay sources listed in A-J, nor will they be part of a funded charity program.</li>
                                  <li>For classifying individual commercial plans, providers should use their best judgement or follow-up with the appropriate commercial or private contact to classify individual commercial plans. For state-specific plans (other than traditional Medicaid) or other government plans (e.g., Tricare or other VA plans), providers should follow-up with the appropriate state or government contacts for advice on classifying these plans.</li>
                                  </ul>`
        };
    },
      provide() {
            return {
                dialogRef: computed(() => this.dialogRef)
            };
        },

    async created() {
        this._centralIntakeService = Container.instance.get(CentralIntakeService);
        this._patientsService = Container.instance.get(PatientService);
        this._toastrService = Container.instance.get(ToastrService);
        this.insurerInfo = {
            primary: {
                isMedicare: null,
                isMedicaid: null,
                partADate: "PrimaryMedicarePartADate",
                partBDate: "PrimaryMedicarePartBDate",
                details: "PrimaryInsuredDetails",
                priorEpisode: "PrimaryExistingPriorEpisode",
                relationship: "PrimaryRelationship",
                isRelationshipNotSelf: null,
                memberID: "PrimaryHealthPlanId",
                groupName: "PrimaryGroupName",
                groupID: "PrimaryGroupId",
                name: "primary"
            },
            secondary: {
                isMedicare: null,
                isMedicaid: null,
                partADate: "SecondaryMedicarePartADate",
                partBDate: "SecondaryMedicarePartBDate",
                details: "SecondaryInsuredDetails",
                priorEpisode: "SecondaryExistingPriorEpisode",
                relationship: "SecondaryRelationship",
                isRelationshipNotSelf: null,
                memberID: "SecondaryHealthPlanId",
                groupName: "SecondaryGroupName",
                groupID: "SecondaryGroupId",
                name: "secondary"
            },
            tertiary: {
                isMedicare: null,
                isMedicaid: null,
                partADate: "TertiaryMedicarePartADate",
                partBDate: "TertiaryMedicarePartBDate",
                details: "TertiaryInsuredDetails",
                priorEpisode: "TertiaryExistingPriorEpisode",
                relationship: "TertiaryRelationship",
                isRelationshipNotSelf: null,
                memberID: "TertiaryHealthPlanId",
                groupName: "TertiaryGroupName",
                groupID: "TertiaryGroupId",
                name: "tertiary"
            }
        };
        this.mbiDuplicateCache = {};
        if (this.isRefer) {
            this.setReferralValues();
        }
        if (this.isEditing) {
            this.referralCache = JSON.parse(JSON.stringify(this.referral));
            this.setReferralValues();
        }
        this.secondaryInsurance = this.referralOptions.secondaryInsurance.filter((item: IGetInsuranceQuery) =>
            Number(item.value)
        );
        this.tertiaryInsurance = this.referralOptions.tertiaryInsurance.filter((item: IGetInsuranceQuery) =>
            Number(item.value)
        );
        const primaryPayors: IGetInsuranceQuery[] = await this.getPrimaryPayors();
        this.primaryInsurance = primaryPayors?.filter((item: IGetInsuranceQuery) => Number(item.value));
        if (this.isEditing) {
            this.setInsurers();
        } else {
            this.referral.Referral.PrimaryRelationship = this.defaultInsurerRelation;
            this.referral.Referral.SecondaryRelationship = this.defaultInsurerRelation;
            this.referral.Referral.TertiaryRelationship = this.defaultInsurerRelation;
        }
    },

    watch: {
        async branchId() {
            const primaryPayors: IGetInsuranceQuery[] = await this.getPrimaryPayors();
            this.primaryInsurance = primaryPayors?.filter((item: IGetInsuranceQuery) => Number(item.value));
        }
    },

    methods: {
        setReferralValues() {
            this.referral.Referral.MbiNumber
                ? (this.payorInfoValues.MbiNumber = this.referral.Referral.MbiNumber)
                : (this.payorInfoNotapplicable.MbiNumber = true);

            this.referral.Referral.MedicaidNumber
                ? (this.payorInfoValues.MedicaidNumber = this.referral.Referral.MedicaidNumber)
                : (this.payorInfoNotapplicable.MedicaidNumber = true);

            this.referral.ReferralExtension.AlternateMedicaidNumber
                ? (this.payorInfoValues.AlternateMedicaidNumber =
                      this.referral.ReferralExtension.AlternateMedicaidNumber)
                : (this.payorInfoNotapplicable.AlternateMedicaidNumber = true);
        },

        setInsurers() {
            this.referral.Referral.PrimaryInsurance = this.referralCache.Referral.PrimaryInsurance;
            if (this.referral.Referral.PrimaryInsurance) {
                this.medicareInsurance.includes(this.referral.Referral.PrimaryInsurance)
                    ? (this.insurerInfo.primary.isMedicare = true)
                    : (this.insurerInfo.primary.isMedicaid = true);
            }
            if (this.referral.Referral.SecondaryInsurance) {
                this.medicareInsurance.includes(this.referral.Referral.SecondaryInsurance)
                    ? (this.insurerInfo.secondary.isMedicare = true)
                    : (this.insurerInfo.secondary.isMedicaid = true);
            }
            if (this.referral.Referral.TertiaryInsurance) {
                this.medicareInsurance.includes(this.referral.Referral.TertiaryInsurance)
                    ? (this.insurerInfo.tertiary.isMedicare = true)
                    : (this.insurerInfo.tertiary.isMedicaid = true);
            }
            setTimeout(() => {
                this.setRelationships();
            });
        },

        setRelationships() {
            this.referral.Referral.PrimaryRelationship =
                this.referralCache.Referral.PrimaryRelationship == null
                    ? this.defaultInsurerRelation
                    : this.referralCache.Referral.PrimaryRelationship;
            if (this.referral.Referral.PrimaryRelationship != this.defaultInsurerRelation) {
                this.insurerInfo.primary.isRelationshipNotSelf = true;
            }

            this.referral.Referral.SecondaryRelationship =
                this.referralCache.Referral.SecondaryRelationship == null
                    ? this.defaultInsurerRelation
                    : this.referralCache.Referral.SecondaryRelationship;

            if (this.referral.Referral.SecondaryRelationship != this.defaultInsurerRelation) {
                this.insurerInfo.secondary.isRelationshipNotSelf = true;
            }

            this.referral.Referral.TertiaryRelationship =
                this.referralCache.Referral.TertiaryRelationship == null
                    ? this.defaultInsurerRelation
                    : this.referralCache.Referral.TertiaryRelationship;

            if (this.referral.Referral.TertiaryRelationship != this.defaultInsurerRelation) {
                this.insurerInfo.tertiary.isRelationshipNotSelf = true;
            }
            setTimeout(() => {
                this.referral.Referral.AdditionalBillingInfo.SecondaryInsuredDetails.InsuredGender =
                    this.referralCache.Referral.AdditionalBillingInfo.SecondaryInsuredDetails.InsuredGender;
                this.referral.Referral.AdditionalBillingInfo.PrimaryInsuredDetails.InsuredGender =
                    this.referralCache.Referral.AdditionalBillingInfo.PrimaryInsuredDetails.InsuredGender;
                this.referral.Referral.AdditionalBillingInfo.TertiaryInsuredDetails.InsuredGender =
                    this.referralCache.Referral.AdditionalBillingInfo.TertiaryInsuredDetails.InsuredGender;
            });
        },

        async payorChanged(id: string, type: string) {
            if (!id) {
                this.insurerInfo[type].isMedicare = false;
                this.insurerInfo[type].isMedicaid = false;
                this.clearFilledValues(type, [Insurer.Medicare, Insurer.NonMedicare]);
                return;
            }
            if (this.medicareInsurance.includes(id)) {
                this.insurerInfo[type].isMedicare = true;
                this.insurerInfo[type].isMedicaid = false;
                if (type == "primary") this.payorInfoNotapplicable.MbiNumber = false;
                this.clearFilledValues(type, [Insurer.NonMedicare]);
            } else {
                this.insurerInfo[type].isMedicare = false;
                this.insurerInfo[type].isMedicaid = true;
                this.clearFilledValues(type, [Insurer.Medicare]);
            }
        },

        clearAddressfields(type: string) {
            this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].FirstName = null;
            this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].MiddleInitial = null;
            this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].LastName = null;
            this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].InsuredDateOfBirth = null;
            this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].InsuredGender = null;
            this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].Address.AddressLine1 = null;
            this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].Address.AddressLine2 = null;
            this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].Address.AddressCity = null;
            this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].Address.AddressStateCode =
                null;
            this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].Address.AddressZipCode = null;
            this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].Address.AddressZipFive = null;
            this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].Address.AddressZipFour = null;
        },

        clearFilledValues(type: string, medType: string[]) {
            medType.forEach((med) => {
                if (med == Insurer.Medicare) {
                    this.referral.ReferralExtension[this.insurerInfo[type].partADate] = null;
                    this.referral.ReferralExtension[this.insurerInfo[type].partBDate] = null;
                    this.referral.ReferralExtension[this.insurerInfo[type].priorEpisode] = null;
                } else if (med == Insurer.NonMedicare) {
                    this.referral.Referral[this.insurerInfo[type].memberID] = null;
                    this.referral.Referral[this.insurerInfo[type].groupName] = null;
                    this.referral.Referral[this.insurerInfo[type].groupID] = null;
                    this.referral.Referral[this.insurerInfo[type].relationship] = null;
                    this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].EmployerName = null;
                    this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].DeductibleAmount =
                        null;
                    this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].DeductibleMet = false;
                    this.referral.Referral.AdditionalBillingInfo[this.insurerInfo[type].details].CopayAmount = null;
                    this.clearAddressfields(type);
                }
            });
        },

        relationshipToEnsuredChanged(type: string, value: number) {
            let showSelfFields: boolean = false;
            if (value) showSelfFields = !(this.relationshipToInsuredEnum.enum.Self == value);
            this.insurerInfo[type].isRelationshipNotSelf = showSelfFields;
            if (showSelfFields) this.clearAddressfields(type);
        },

        payorInfoChanged(referralObject: IHomeHealthReferralOld | IHomeHealthReferralExtension, id: string) {
            if (!referralObject || !id) return;
            if ((this.payorInfoNotapplicable as any)[id]) {
                referralObject[id] = "";
                (this.payorInfoValues as any)[id] = "";
            } else {
                referralObject[id] = (this.payorInfoValues as any)[id];
            }
        },

        async getPrimaryPayors() {
            if (!this.referral.Referral.BranchId || !this.referral.agencyId) return [];
            const query: IGetPayorByBranchID = {
                branchId: this.referral.Referral.BranchId,
                agencyId: this.referral.agencyId
            };
            return await this._centralIntakeService.getReferralPayorsByBranchId(query);
        },

        async validateMBI(currentValue: string) {
            if (!(currentValue in this.mbiDuplicateCache)) {
                const payload: IPatientValidation = {
                    AgencyId: this.referral.agencyId,
                    Application: this.referral.application,
                    DuplicateCheckViewData: {
                        MbiNumber: currentValue
                    }
                };
                const data = await this._patientsService.checkDuplicates(payload);
                this.mbiDuplicateCache[currentValue] = !data.isDuplicate;
                return !data.isDuplicate;
            }
            return this.mbiDuplicateCache[currentValue];
        },

        async verifyEligibility(){
             try {
                const missingFields=[];
                if(!this.referral.Referral.MbiNumber) missingFields.push("MBI Number");
                if(!this.referral.Referral.LastName) missingFields.push("Last Name");
                if(!this.referral.Referral.FirstName) missingFields.push("First Name");
                if(!this.referral.Referral.DOB) missingFields.push("Date of birth");
                if(!this.referral.Referral.Gender) missingFields.push("gender");
                if(missingFields.length > 0) {
                    const displayedFields = missingFields.map(item=>`•${item}`).join("<br>");
                    this._toastrService.error({
                        title:"Missing Required Fields",
                        message:`Please provide the following before verifying eligibility:<br>${displayedFields}`
                    });
                    return
                }
                  const response = await this._patientsService.getverifyEligibility({
                            agencyId: this.referral.agencyId,
                            medicareNumber: this.referral.Referral.MbiNumber,
                            lastName: this.referral.Referral.LastName,
                            firstName: this.referral.Referral.FirstName,
                            dob: this.referral.Referral.DOB,
                            gender:this.referral.Referral.Gender
                         });
                           this.dialogRef = this.$dialog.open(VerifyEligibility, {
                 props: {
                    modal: true,
                    showHeader: false,
                    style: "width: 950px"
                },
                data: {
                    response,
                    AgencyId:this.referral.agencyId
                },

            });
                    } catch (error) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while verifying this referral's eligibility. Please try again.`
                });
            }
        },

        setMbiNumber(value: string) {
            this.payorInfoValues.MbiNumber = value.toUpperCase();
        }
    }
});
